<template>
  <div class="premium-dt-wrap">
    <!-- Label -->
    <label v-if="label" class="premium-label">{{ label }}</label>

    <v-menu
      ref="menu"
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      left
      min-width="auto"
      :nudge-bottom="4"
    >
      <!-- ✅ Activator — same style as PremiumDatePicker -->
      <template #activator="{ on, attrs }">
        <div
          class="premium-dt-trigger"
          :class="{
            'is-active': menu,
            'is-filled': !!value,
            'has-error': !!error,
          }"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon size="18" class="icon-main"
            >mdi-calendar-clock-outline</v-icon
          >
          <span class="dt-text">{{ displayValue }}</span>

          <button
            v-if="value && !menu"
            class="clear-btn"
            @click.stop="clearValue"
            type="button"
          >
            <v-icon size="14">mdi-close</v-icon>
          </button>
          <v-icon v-else size="16" class="icon-chevron"
            >mdi-chevron-down</v-icon
          >
        </div>
      </template>

      <!-- ✅ Dropdown card -->
      <div class="premium-dt-card">
        <!-- ── SIDE BY SIDE BODY ── -->
        <div class="dt-body">
          <!-- LEFT: Date Picker -->
          <div class="dt-left">
            <v-date-picker
              v-model="datePart"
              no-title
              scrollable
              :color="primaryColor"
              :min="min"
              class="premium-picker"
            />
          </div>

          <!-- Vertical divider -->
          <div class="v-divider-line" />

          <!-- RIGHT: Time Picker -->
          <div class="dt-right">
            <!-- HH : MM switcher -->
            <div class="time-display-row">
              <v-icon size="17" color="primary" class="mr-2"
                >mdi-clock-outline</v-icon
              >

              <button
                class="time-part-btn"
                :class="{ 'is-active': timeFocus === 'hour' }"
                @click="timeFocus = 'hour'"
                type="button"
              >
                {{ String(selectedHour).padStart(2, "0") }}
              </button>

              <span class="time-colon">:</span>

              <button
                class="time-part-btn"
                :class="{ 'is-active': timeFocus === 'minute' }"
                @click="timeFocus = 'minute'"
                type="button"
              >
                {{ String(selectedMinute).padStart(2, "0") }}
              </button>
            </div>

            <!-- Hours grid (00–23, 3 per row) -->
            <div v-if="timeFocus === 'hour'" class="time-grid">
              <button
                v-for="h in hours"
                :key="h"
                type="button"
                class="time-cell"
                :class="{ 'is-selected': selectedHour === h }"
                @click="selectHour(h)"
              >
                {{ String(h).padStart(2, "0") }}
              </button>
            </div>

            <!-- Minutes grid (00, 05 … 55, 3 per row) -->
            <div v-else class="time-grid">
              <button
                v-for="m in minutes"
                :key="m"
                type="button"
                class="time-cell"
                :class="{ 'is-selected': selectedMinute === m }"
                @click="selectMinute(m)"
              >
                {{ String(m).padStart(2, "0") }}
              </button>
            </div>

            <!-- Clock hint icon -->
            <!-- <div class="time-footer-icon">
              <v-icon size="18" color="#CBD5E1">mdi-clock-outline</v-icon>
            </div> -->
          </div>
        </div>

        <!-- ── Actions ── -->
        <v-divider />
        <div class="dt-actions">
          <v-spacer />
          <v-btn text class="btn-premium-cancel" @click="cancel">Cancel</v-btn>
          <v-btn
            depressed
            class="btn-premium-primary ml-2"
            :style="{ background: brandColor }"
            @click="save"
          >
            OK
          </v-btn>
        </div>
      </div>
    </v-menu>

    <!-- Error -->
    <div v-if="error" class="error-msg">
      {{ Array.isArray(error) ? error[0] : error }}
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";

export default {
  name: "PremiumDateTimePicker",

  props: {
    value: { default: null },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "Select Date & Time" },
    format: { type: String, default: "DD MMM, YYYY · hh:mm A" },
    error: { type: [String, Array], default: "" },
    min: { type: String, default: null },
  },

  data() {
    return {
      menu: false,
      timeFocus: "hour",
      datePart: null,
      selectedHour: 0,
      selectedMinute: 0,
      hours: Array.from({ length: 24 }, (_, i) => i),
      minutes: Array.from({ length: 12 }, (_, i) => i * 5),
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#0d9972";
    },
    displayValue() {
      if (!this.value) return this.placeholder;
      return moment(this.value).format(this.format);
    },
  },

  watch: {
    menu(opened) {
      if (opened) {
        this.timeFocus = "hour";
        this.initParts();
      }
    },
  },

  methods: {
    initParts() {
      const m = this.value ? moment(this.value) : moment();
      this.datePart = m.format("YYYY-MM-DD");
      this.selectedHour = m.hour();
      this.selectedMinute = Math.floor(m.minute() / 5) * 5;
    },

    selectHour(h) {
      this.selectedHour = h;
      this.$nextTick(() => {
        this.timeFocus = "minute";
      });
    },

    selectMinute(m) {
      this.selectedMinute = m;
    },

    save() {
      if (!this.datePart) return;
      const hh = String(this.selectedHour).padStart(2, "0");
      const mm = String(this.selectedMinute).padStart(2, "0");
      const combined = moment(
        `${this.datePart} ${hh}:${mm}`,
        "YYYY-MM-DD HH:mm",
      ).toDate();
      this.$emit("input", combined);
      this.$emit("change", combined);
      this.menu = false;
    },

    cancel() {
      this.initParts();
      this.menu = false;
    },

    clearValue() {
      this.$emit("input", null);
      this.$emit("change", null);
      this.datePart = null;
      this.selectedHour = 0;
      this.selectedMinute = 0;
    },
  },
};
</script>

<style scoped>
/* ── Wrapper ── */
.premium-dt-wrap {
  margin-bottom: 4px;
  width: 100%;
}

/* ── Label ── */
.premium-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}

/* ── Trigger ── */
.premium-dt-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 14px;
  background: var(--surface);
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1));
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  position: relative;
}

.premium-dt-trigger:hover {
  border-color: var(--brand, #0d9972);
}
.premium-dt-trigger.is-active {
  border-color: var(--brand, #0d9972);
  box-shadow: 0 0 0 3px rgba(13, 153, 114, 0.12);
}
.premium-dt-trigger.has-error {
  border-color: #ef4444;
}
.premium-dt-trigger.is-filled {
  color: var(--tx, #111827);
}

.icon-main {
  color: var(--tx3, #9ca3af) !important;
  flex-shrink: 0;
  transition: color 0.2s;
}
.premium-dt-trigger.is-active .icon-main {
  color: var(--brand, #0d9972) !important;
}
.premium-dt-trigger.is-filled .icon-main {
  color: var(--brand, #0d9972) !important;
}

.dt-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-chevron {
  color: var(--tx4, #9ca3af) !important;
  flex-shrink: 0;
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  margin-left: auto;
}
.premium-dt-trigger.is-active .icon-chevron {
  transform: rotate(180deg);
}

.clear-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  color: var(--tx3) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: auto;
}
.clear-btn:hover {
  background: var(--brand);
  color: #fff !important;
}

/* ── Dropdown card ── */
.premium-dt-card {
  background: var(--surface, #fff);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.14);
}

/* ── Side-by-side body ── */
.dt-body {
  display: flex;
  align-items: stretch;
}

/* Left: date picker */
.dt-left {
  flex-shrink: 0;
}

/* Remove internal card shadow/radius from Vuetify picker */
::v-deep .premium-picker.v-picker {
  box-shadow: none !important;
  border-radius: 0 !important;
}

/* Vertical divider between date & time */
.v-divider-line {
  width: 1px;
  background: var(--border, rgba(0, 0, 0, 0.08));
  align-self: stretch;
  flex-shrink: 0;
}

/* Right: time picker */
.dt-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 14px 8px;
  min-width: 160px;
  /* height: 280px; */
}

/* HH : MM row */
.time-display-row {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.time-part-btn {
  min-width: 46px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1.5px solid transparent;
  background: var(--surface2, #f1f5f9);
  font-size: 18px;
  font-weight: 700;
  color: var(--tx, #111827);
  cursor: pointer;
  transition: all 0.18s ease;
  text-align: center;
  line-height: 1.25;
}
.time-part-btn:hover {
  background: rgba(255, 255, 255, 0.06);
}
.time-part-btn.is-active {
  border-color: var(--brand, #0d9972);
  background: rgba(13, 153, 114, 0.08);
  color: var(--brand, #0d9972);
}

.time-colon {
  font-size: 20px;
  font-weight: 700;
  color: var(--tx3, #94a3b8);
  padding: 0 5px;
}

/* ── Time grid ── */
.time-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  flex: 1;
  max-height: 220px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.12) transparent;
}
.time-grid::-webkit-scrollbar {
  width: 4px;
}
.time-grid::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.time-grid::-webkit-scrollbar-track {
  background: transparent;
}

.time-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.time-cell:hover {
  background: var(--surface2, #f1f5f9);
}
.time-cell.is-selected {
  background: var(--btn-color, #0d9972);
  color: #fff;
}

/* Clock hint */
.time-footer-icon {
  display: flex;
  justify-content: center;
  padding-top: 10px;
}

/* ── Actions ── */
.dt-actions {
  display: flex;
  align-items: center;
  padding: 8px 12px 10px;
}

/* .btn-premium-cancel {
    color: var(--tx2, #475569) !important;
    font-size: 13px !important;
    font-weight: 700 !important;
    text-transform: none !important;
    letter-spacing: 0.02em !important;
    border-radius: 8px !important;
  }
  .btn-premium-cancel:hover {
    background: var(--surface2, #f8fafc) !important;
  } */

.btn-premium-primary {
  color: #ffffff !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
  min-width: 60px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}
.btn-premium-primary:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

/* ── Error ── */
.error-msg {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
  padding-left: 4px;
}

/* ── Dark mode ── */
.theme--dark .premium-dt-trigger {
  background: #273548;
  border-color: rgba(255, 255, 255, 0.1);
}
.theme--dark .premium-dt-trigger:hover,
.theme--dark .premium-dt-trigger.is-active {
  background: #2f3f57;
  border-color: var(--brand);
}
.theme--dark .premium-dt-trigger.is-filled {
  color: #f1f5f9;
}
.theme--dark .premium-dt-card {
  background: var(--surface2, #273548);
}
.theme--dark .v-divider-line {
  background: rgba(255, 255, 255, 0.08);
}
.theme--dark .time-part-btn {
  background: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}
.theme--dark .time-cell {
  color: #cbd5e1;
}
.theme--dark .time-cell:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* ── Dark mode: v-date-picker calendar ── */
.theme--dark ::v-deep .premium-picker.v-picker {
  background: #1e293b !important;
}
.theme--dark ::v-deep .premium-picker .v-picker__body {
  background: #1e293b !important;
}
.theme--dark ::v-deep .premium-picker .v-date-picker-table {
  background: #1e293b !important;
}
.theme--dark ::v-deep .premium-picker .v-date-picker-table table thead th {
  color: #64748b !important;
}
.theme--dark ::v-deep .premium-picker .v-date-picker-table table tbody td button:not(.v-btn--active) {
  color: #cbd5e1 !important;
}
.theme--dark ::v-deep .premium-picker .v-date-picker-table table tbody td button:hover:not(.v-btn--active) {
  background: rgba(255, 255, 255, 0.07) !important;
}
.theme--dark ::v-deep .premium-picker .v-date-picker-header {
  background: #1e293b !important;
}
.theme--dark ::v-deep .premium-picker .v-date-picker-header .v-btn {
  color: #94a3b8 !important;
}
.theme--dark ::v-deep .premium-picker .v-date-picker-header__value button {
  color: #f1f5f9 !important;
}
/* Time grid scrollbar dark */
.theme--dark .time-grid::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}
</style>

<style>
/* ── PremiumDateTimePicker: calendar dark mode (global — scoped cannot reach) ── */
.theme--dark .premium-picker.v-picker,
.theme--dark .premium-picker .v-picker__body,
.theme--dark .premium-picker .v-date-picker-table,
.theme--dark .premium-picker .v-date-picker-header {
  background: #1e293b !important;
}
.theme--dark .premium-picker .v-date-picker-table table thead th {
  color: #64748b !important;
}
.theme--dark .premium-picker .v-date-picker-table table tbody td button:not(.v-btn--active) {
  color: #cbd5e1 !important;
}
.theme--dark .premium-picker .v-date-picker-table table tbody td button:hover:not(.v-btn--active) {
  background: rgba(255, 255, 255, 0.07) !important;
}
.theme--dark .premium-picker .v-date-picker-header .v-btn {
  color: #94a3b8 !important;
}
.theme--dark .premium-picker .v-date-picker-header__value button {
  color: #f1f5f9 !important;
  font-weight: 700;
}
.theme--dark .premium-picker .v-btn--active {
  color: #ffffff !important;
}
</style>
